/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  CreateRoleRequest,
  DeleteAdminV1OrgMembersIdPathParameters,
  DeleteAdminV1OrgRolesIdPathParameters,
  GetAdminV1OrgMembers200,
  GetAdminV1OrgMembersIdEffectivePermissionsPathParameters,
  GetAdminV1OrgRoles200,
  Member,
  MemberEffectivePermissions,
  PatchAdminV1OrgMembersIdBody,
  PatchAdminV1OrgMembersIdPathParameters,
  PatchAdminV1OrgRolesIdPathParameters,
  PostAdminV1OrgMembersIdResendInvitationPathParameters,
  PostAdminV1OrgMembersInvitationsBody,
  Role,
  UpdateRoleRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary Invited and active members of the organization
 */
export const getAdminV1OrgMembers = (

 options?: SecondParameter<typeof customInstance<GetAdminV1OrgMembers200>>,) => {
      return customInstance<GetAdminV1OrgMembers200>(
      {url: `/admin/v1/org/members`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Roles a member can hold: system roles plus the organization's own
 */
export const getAdminV1OrgRoles = (

 options?: SecondParameter<typeof customInstance<GetAdminV1OrgRoles200>>,) => {
      return customInstance<GetAdminV1OrgRoles200>(
      {url: `/admin/v1/org/roles`, method: 'GET'
    },
      options);
    }
  /**
 * Permissions come from the catalog and may not exceed the Admin role (`*` and org.write stay with owners) nor what you hold yourself. 409 ALREADY_EXISTS on body.key when a system or custom role has the key; at most 50 custom roles
 * @summary Create a custom role
 */
export const postAdminV1OrgRoles = (
    createRoleRequest: BodyType<CreateRoleRequest>,
 options?: SecondParameter<typeof customInstance<Role>>,) => {
      return customInstance<Role>(
      {url: `/admin/v1/org/roles`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createRoleRequest
    },
      options);
    }
  /**
 * Takes effect on the next request of every member holding the role. 409 CONFLICT for a system role
 * @summary Rename a custom role or change its permissions
 */
export const patchAdminV1OrgRolesId = (
    { id }: PatchAdminV1OrgRolesIdPathParameters,
    updateRoleRequest: BodyType<UpdateRoleRequest>,
 options?: SecondParameter<typeof customInstance<Role>>,) => {
      return customInstance<Role>(
      {url: `/admin/v1/org/roles/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateRoleRequest
    },
      options);
    }
  /**
 * 409 RESOURCE_IN_USE (meta.members) while invited or active members hold it; 409 CONFLICT for a system role
 * @summary Delete a custom role nobody holds
 */
export const deleteAdminV1OrgRolesId = (
    { id }: DeleteAdminV1OrgRolesIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/org/roles/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Creates the membership in invited status and emails a 7-day link. An existing user keeps their password; a new one sets it on accept. Re-inviting a removed or pending member refreshes the link. Only owners can assign the owner role, and nobody assigns a role or grants a permission beyond their own (403 PERMISSION_DENIED). Extra and removed permissions and per-store roles are optional; the owner takes none (422 OWNER_ACCESS_FIXED)
 * @summary Invite a person by email
 */
export const postAdminV1OrgMembersInvitations = (
    postAdminV1OrgMembersInvitationsBody: BodyType<PostAdminV1OrgMembersInvitationsBody>,
 options?: SecondParameter<typeof customInstance<Member>>,) => {
      return customInstance<Member>(
      {url: `/admin/v1/org/members/invitations`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1OrgMembersInvitationsBody
    },
      options);
    }
  /**
 * @summary Send a fresh invitation link to a pending member
 */
export const postAdminV1OrgMembersIdResendInvitation = (
    { id }: PostAdminV1OrgMembersIdResendInvitationPathParameters,
 options?: SecondParameter<typeof customInstance<Member>>,) => {
      return customInstance<Member>(
      {url: `/admin/v1/org/members/${id}/resend-invitation`, method: 'POST'
    },
      options);
    }
  /**
 * Omitted fields keep their value; `storeRoles` replaces the whole set, and per-store roles for stores the member loses access to go. You cannot change yourself; the last owner cannot be demoted; only owners can change owners; nobody assigns a role or grants a permission beyond their own (403 PERMISSION_DENIED); the owner takes no overrides (422 OWNER_ACCESS_FIXED). Takes effect on the member's next request
 * @summary Change a member's role, store access, extra or removed permissions, or per-store roles
 */
export const patchAdminV1OrgMembersId = (
    { id }: PatchAdminV1OrgMembersIdPathParameters,
    patchAdminV1OrgMembersIdBody: BodyType<PatchAdminV1OrgMembersIdBody>,
 options?: SecondParameter<typeof customInstance<Member>>,) => {
      return customInstance<Member>(
      {url: `/admin/v1/org/members/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1OrgMembersIdBody
    },
      options);
    }
  /**
 * @summary Remove a member and end their sessions
 */
export const deleteAdminV1OrgMembersId = (
    { id }: DeleteAdminV1OrgMembersIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/org/members/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Per store: the store's own role or the default, the permissions that apply (from the role or added for the member) and the role permissions taken away. `organization` is what organization-level screens (master catalog, team, settings) allow: every grant joined
 * @summary What a member can do in each store, and where each permission comes from
 */
export const getAdminV1OrgMembersIdEffectivePermissions = (
    { id }: GetAdminV1OrgMembersIdEffectivePermissionsPathParameters,
 options?: SecondParameter<typeof customInstance<MemberEffectivePermissions>>,) => {
      return customInstance<MemberEffectivePermissions>(
      {url: `/admin/v1/org/members/${id}/effective-permissions`, method: 'GET'
    },
      options);
    }
  export type GetAdminV1OrgMembersResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgMembers>>>
export type GetAdminV1OrgRolesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgRoles>>>
export type PostAdminV1OrgRolesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgRoles>>>
export type PatchAdminV1OrgRolesIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgRolesId>>>
export type DeleteAdminV1OrgRolesIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1OrgRolesId>>>
export type PostAdminV1OrgMembersInvitationsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgMembersInvitations>>>
export type PostAdminV1OrgMembersIdResendInvitationResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgMembersIdResendInvitation>>>
export type PatchAdminV1OrgMembersIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgMembersId>>>
export type DeleteAdminV1OrgMembersIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1OrgMembersId>>>
export type GetAdminV1OrgMembersIdEffectivePermissionsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgMembersIdEffectivePermissions>>>
